import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import { TopBar } from '@openx/components-table/ChipFilters/TopBar';
import type { DataTypeForecasting, SortingKeysForecasting } from '@openx/types/dealForecasting';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../../../utils/context/DealContext';
import { StyledTableCell } from '../../../styledShared/StyledTableCell';
import { StyledTableRow } from '../../../styledShared/StyledTableRow';
import { SortDirection, type TableColumns, type TableState, UnforecastedTargetingSectionTab } from '../../../types';
import {
  dispatchChangeSortingDirection,
  dispatchInitialData,
  dispatchPhraseChange,
  displayData,
  getInitSortingColumn,
  getPlaceholderText,
  processType,
  reducer,
  removeRecordsWithNullValues,
} from '../utils/utils';

import { StyledArrowDownward, StyledArrowUpward } from './styled/StyledArrows';
import { StyledShowMoreButton } from './styled/StyledShowMoreButton';
import { StyledTableContainer } from './styled/StyledTableContainer';
import { StyledTopBarContainer } from './styled/StyledTopBarContainer';

const columns: TableColumns = {
  [UnforecastedTargetingSectionTab.AdSize]: ['ad_size', 'fraction'],
  [UnforecastedTargetingSectionTab.VideoDuration]: ['minduration', 'maxduration', 'fraction'],
  [UnforecastedTargetingSectionTab.USCity]: ['city', 'state', 'fraction'],
};

type UnforecastedTargetingTableProps = {
  type: UnforecastedTargetingSectionTab;
};

export const UnforecastedTargetingTable = ({ type }: UnforecastedTargetingTableProps) => {
  const { useDealForecast } = useDealContext();
  const {
    forecastState: { forecast },
  } = useDealForecast({ shallow: true });
  const { t } = useTranslation();
  const [offset, setOffset] = useState(100);
  const sanitizedData = useMemo(() => removeRecordsWithNullValues(forecast?.[type] || []), [forecast, type]);
  const initState: TableState = useMemo(
    () => ({
      data: sanitizedData,
      filters: { phrase: '', sort: { column: getInitSortingColumn(sanitizedData), dir: SortDirection.DESC } },
      type,
    }),
    [sanitizedData, type]
  );

  const [state, dispatch] = useReducer(reducer, initState);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      dispatchInitialData(initState, state.filters.sort, type, dispatch);
    }
  }, [initState, state.filters.sort, type]);

  const { data, filters: filtersState } = state;
  const displayedData = data.slice(0, offset);
  const minimalTopBarInputLength = type === UnforecastedTargetingSectionTab.AdSize ? 1 : 2;

  const renderSortArrow = useCallback((column: string, filtersState: TableState['filters']) => {
    const isColumnActiveSorting = filtersState.sort.column === column;
    if (filtersState.sort.dir === SortDirection.ASC && isColumnActiveSorting) {
      return <StyledArrowUpward />;
    }

    return isColumnActiveSorting && <StyledArrowDownward />;
  }, []);

  const renderTable = () => {
    if (data.length === 0) {
      return <Typography data-test="empty-section">{t('No data received')}</Typography>;
    }

    return (
      <StyledTableContainer>
        <Table stickyHeader data-test={`forecasting-table-${type}`}>
          <TableHead>
            <StyledTableRow data-test={'table-header'}>
              {columns[type].map(key => (
                <StyledTableCell
                  active={key === filtersState.sort.column}
                  data-test={'table-cell'}
                  headerCell
                  onClick={() => dispatchChangeSortingDirection(key as SortingKeysForecasting, state, dispatch)}
                  key={key}
                >
                  {processType(type)[key]} {renderSortArrow(key, filtersState)}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>

          <TableBody data-test="table-body">
            {displayedData.map((row: DataTypeForecasting, index) => (
              <StyledTableRow data-test="table-row" key={`${type}-${index}`} noBorder>
                {columns[type].map((key, index) => (
                  <StyledTableCell data-test="table-cell" denseCells key={`${key}-${index}`}>
                    {displayData(key, row)}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>

          {offset < data.length && (
            <TableFooter>
              <tr>
                <td>
                  <StyledShowMoreButton
                    data-test="show-more-button"
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setOffset(offset + 100);
                    }}
                  >
                    {t('Show More')}
                  </StyledShowMoreButton>
                </td>
              </tr>
            </TableFooter>
          )}
        </Table>
      </StyledTableContainer>
    );
  };

  const displayTopBar =
    type === UnforecastedTargetingSectionTab.AdSize || type === UnforecastedTargetingSectionTab.USCity;
  const { phrase } = filtersState;

  return (
    <>
      {displayTopBar && (
        <StyledTopBarContainer>
          <TopBar
            autofocus={false}
            fullWidth
            minLength={minimalTopBarInputLength}
            filters={{
              phrase,
            }}
            onChange={(filters: { phrase: string }) => {
              if (filters.phrase.length > minimalTopBarInputLength) {
                dispatchPhraseChange(filters.phrase, initState, type, dispatch);
                return;
              }

              dispatchInitialData(initState, state.filters.sort, type, dispatch);
            }}
            searchText={getPlaceholderText(type)}
          />
        </StyledTopBarContainer>
      )}

      {renderTable()}
    </>
  );
};
